/**
 * A party's money-in, in the two books a shop actually keeps.
 *
 *   · Payments — the receipts themselves: what came in, which invoices each one
 *     settled, and the printable voucher the customer is handed.
 *   · Accountant — سند قبض / سند دفع: the cash vouchers posted against this
 *     party from the accounting side. Not the same records as the payments
 *     above (a payment settles a document; a voucher moves cash against an
 *     account), and reconciling a customer means holding both at once — so
 *     they are two views of ONE tab rather than two tabs that look like
 *     duplicates of each other.
 *
 * Each view fetches only when it is opened: Radix unmounts the inactive one.
 */
import type { PortalPaymentSummary, Voucher } from '@erp/shared';
import { Banknote, Printer, Receipt } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Card, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/shadcn/index.js';
import { Button, EmptyState } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { appUrl } from '../../lib/basePath.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { listVouchers } from '../../lib/vouchers.js';

/**
 * A payment as the payments endpoint returns it — the statement's own summary
 * plus what the server resolved about the settlement. The extra fields are
 * optional because the fallback rows (see below) genuinely do not have them.
 */
interface PaymentRow extends PortalPaymentSummary {
  direction?: string | null;
  status?: string;
  settles?: string[];
  allocationCount?: number;
}

/** Open a print view in a tab that KEEPS the opener, so it inherits the session. */
function openPrint(path: string): void {
  // Not an <a target="_blank">: anchors get implicit noopener, and the print tab
  // would land on the login screen instead of the document.
  window.open(appUrl(path), '_blank');
}

export function PartyPaymentsTab({ partyId, fallback, loading }: {
  partyId: string;
  /**
   * Statement-derived rows. They carry no settlement detail, but they need no
   * `payments.read` either — so a sales clerk keeps the list they have today
   * instead of losing the tab to a permission they were never given.
   */
  fallback: PortalPaymentSummary[];
  loading: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation('parties');
  const { t: td } = useTranslation('documents');
  const canReadPayments = usePermission('payments.read');
  const canReadGl = usePermission('gl.read');
  const [view, setView] = useState('payments');
  const [rows, setRows] = useState<PaymentRow[] | null>(null);

  useEffect(() => {
    if (!canReadPayments) return undefined;
    let on = true;
    void api()
      .get<{ items: PaymentRow[] }>('/payments', { params: { partyId, limit: 500 } })
      .then((r) => { if (on) setRows(r.data.items); })
      // Non-fatal: the statement rows below still render the payment history.
      .catch(() => { if (on) setRows(null); });
    return () => { on = false; };
  }, [partyId, canReadPayments]);

  const money = (a: string, c: string): string => formatMoney(a, { locale: i18n.language, currency: c });
  const list: PaymentRow[] = rows ?? fallback;
  // Only the enriched rows can say what a payment settled; the fallback cannot,
  // and an always-empty column reads as broken rather than as unavailable.
  const showSettles = rows !== null;

  return (
    <Tabs value={view} onValueChange={setView}>
      <TabsList className="mb-3 h-9">
        <TabsTrigger value="payments" className="gap-1.5">
          <Banknote className="h-4 w-4" aria-hidden />{t('tabs.payments', { defaultValue: 'Payments' })}
        </TabsTrigger>
        {canReadGl && (
          <TabsTrigger value="accountant" className="gap-1.5">
            <Receipt className="h-4 w-4" aria-hidden />{t('paymentsTab.accountant', { defaultValue: 'Accountant' })}
          </TabsTrigger>
        )}
      </TabsList>

      <TabsContent value="payments">
        {loading && rows === null ? (
          <Card className="p-4"><Skeleton className="h-40 w-full" /></Card>
        ) : list.length === 0 ? (
          <Card className="p-6"><EmptyState title={t('payments.empty', { defaultValue: 'No payments yet.' })} /></Card>
        ) : (
          <Card className="overflow-x-auto p-0">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-border bg-bg-subtle text-fg-muted">
                <tr>
                  <th className="px-4 py-2.5 text-start">{t('payments.number', { defaultValue: 'Number' })}</th>
                  <th className="px-4 py-2.5 text-start">{t('payments.date', { defaultValue: 'Date' })}</th>
                  <th className="px-4 py-2.5 text-start">{t('payments.method', { defaultValue: 'Method' })}</th>
                  {showSettles && (
                    <th className="px-4 py-2.5 text-start">
                      {td('payments.voucher.settles', { defaultValue: 'In settlement of' })}
                    </th>
                  )}
                  <th className="px-4 py-2.5 text-end">{t('payments.amount', { defaultValue: 'Amount' })}</th>
                  {canReadPayments && <th className="w-12 px-4 py-2.5" />}
                </tr>
              </thead>
              <tbody>
                {list.map((p) => {
                  const nums = p.settles ?? [];
                  const extra = (p.allocationCount ?? nums.length) - nums.length;
                  const out = p.direction === 'outbound';
                  return (
                    <tr key={p.id} className="border-b border-border/60 last:border-0 hover:bg-bg-subtle/50">
                      <td className="px-4 py-2.5 font-mono font-medium">{p.docNumber}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-fg-muted">
                        {formatDate(p.paymentDate, { locale: i18n.language })}
                      </td>
                      <td className="px-4 py-2.5 text-fg-muted">
                        {td(`payments.voucher.method.${(p.method ?? 'cash').toLowerCase()}`, { defaultValue: p.method ?? 'Cash' })}
                      </td>
                      {showSettles && (
                        <td className="px-4 py-2.5 font-mono text-xs">
                          {nums.length === 0 ? (
                            <span className="text-fg-subtle">
                              {td('payments.voucher.onAccount', { defaultValue: 'On account' })}
                            </span>
                          ) : (
                            <>
                              {nums.join(' · ')}
                              {extra > 0 && <span className="text-fg-muted"> +{extra}</span>}
                            </>
                          )}
                        </td>
                      )}
                      <td className={`px-4 py-2.5 text-end font-mono font-semibold tabular-nums ${out ? 'text-warning' : 'text-success'}`}>
                        {money(p.amount, p.currencyCode)}
                      </td>
                      {canReadPayments && (
                        <td className="px-4 py-2.5 text-end">
                          {/* The paper for THIS receipt — the thing a customer
                              asks for at the counter, one click from their file. */}
                          <Button
                            variant="ghost"
                            size="sm"
                            title={t('paymentsTab.openVoucher', { defaultValue: 'Open receipt' })}
                            aria-label={`${t('paymentsTab.openVoucher', { defaultValue: 'Open receipt' })} ${p.docNumber}`}
                            onClick={() => openPrint(`/payments/${p.id}/print`)}
                          >
                            <Printer className="h-4 w-4" aria-hidden />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </TabsContent>

      {canReadGl && (
        <TabsContent value="accountant">
          <PartyVouchersView partyId={partyId} />
        </TabsContent>
      )}
    </Tabs>
  );
}

/**
 * سند قبض / سند دفع raised against this party. Its own component at module
 * scope, NOT nested inside the tab: a component declared inside another is a
 * new type on every parent render, so React would tear it down and refetch the
 * vouchers each time the tab re-rendered.
 */
function PartyVouchersView({ partyId: id }: { partyId: string }): JSX.Element {
  const { t, i18n } = useTranslation('parties');
  const { t: tv } = useTranslation('vouchers');
  const money = (a: string, c: string): string => formatMoney(a, { locale: i18n.language, currency: c });
  const [vouchers, setVouchers] = useState<Voucher[] | null>(null);
  useEffect(() => {
    let on = true;
    void listVouchers({ partyId: id })
      .then((v) => { if (on) setVouchers(v); })
      .catch(() => { if (on) setVouchers([]); });
    return () => { on = false; };
  }, [id]);

  if (!vouchers) return <Card className="p-4"><Skeleton className="h-40 w-full" /></Card>;
  if (vouchers.length === 0) {
    return (
      <Card className="p-6">
        <EmptyState
          title={t('paymentsTab.noVouchers', { defaultValue: 'No vouchers for this party yet.' })}
          description={t('paymentsTab.noVouchersHint', {
            defaultValue: 'Receipt and payment vouchers recorded against this party in accounting will appear here.',
          })}
        />
      </Card>
    );
  }
  return (
    <Card className="overflow-x-auto p-0">
      <table className="w-full text-start text-sm">
        <thead className="border-b border-border bg-bg-subtle text-fg-muted">
          <tr>
            <th className="px-4 py-2.5 text-start">{tv('columns.number', { defaultValue: 'No.' })}</th>
            <th className="px-4 py-2.5 text-start">{tv('columns.date', { defaultValue: 'Date' })}</th>
            <th className="px-4 py-2.5 text-start">{t('paymentsTab.voucherType', { defaultValue: 'Type' })}</th>
            <th className="px-4 py-2.5 text-start">{t('paymentsTab.counterAccount', { defaultValue: 'Account' })}</th>
            <th className="px-4 py-2.5 text-end">{tv('columns.amount', { defaultValue: 'Amount' })}</th>
            <th className="px-4 py-2.5 text-start">{tv('columns.status', { defaultValue: 'Status' })}</th>
            <th className="w-12 px-4 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {vouchers.map((v) => (
            <tr key={v.id} className="border-b border-border/60 last:border-0 hover:bg-bg-subtle/50">
              <td className="px-4 py-2.5 font-mono font-medium">{v.voucherNumber}</td>
              <td className="px-4 py-2.5 whitespace-nowrap text-fg-muted">
                {formatDate(v.voucherDate, { locale: i18n.language })}
              </td>
              <td className="px-4 py-2.5">
                {/* Money in vs money out, coloured the same way every other
                    money surface here does it. */}
                <Badge variant={v.voucherType === 'receipt' ? 'success' : 'warning'}>
                  {tv(`${v.voucherType}.title`, { defaultValue: v.voucherType })}
                </Badge>
              </td>
              <td className="px-4 py-2.5 text-fg-muted">{v.counterAccountName ?? '—'}</td>
              <td className={`px-4 py-2.5 text-end font-mono font-semibold tabular-nums ${v.status === 'void' ? 'text-fg-muted line-through' : v.voucherType === 'receipt' ? 'text-success' : 'text-warning'}`}>
                {money(v.amount, v.currencyCode)}
              </td>
              <td className="px-4 py-2.5">
                <Badge variant={v.status === 'posted' ? 'success' : 'secondary'}>
                  {tv(`status.${v.status}`, { defaultValue: v.status })}
                </Badge>
              </td>
              <td className="px-4 py-2.5 text-end">
                {v.journalEntryId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    title={tv('viewVoucher', { defaultValue: 'View journal voucher' })}
                    aria-label={`${tv('viewVoucher', { defaultValue: 'View journal voucher' })} ${v.voucherNumber}`}
                    onClick={() => openPrint(`/accounting/journals/${v.journalEntryId}/print`)}
                  >
                    <Printer className="h-4 w-4" aria-hidden />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
